/**
 * Which sensor of each function the run treated as primary, and where its
 * wheel sits — keyed by ANT device number, for the health check cards.
 *
 * The cards are titled by SensorIntentSlots slot ("Speed", "Speed 2"), and
 * that slot is not the pipeline's primary/alt ordering. A run whose solve
 * read speed from the sensor in slot 2 shows "Speed 2" as its primary, which
 * is exactly the thing worth saying out loud — and it is the same answer the
 * Aux Sensor and High Rate legends give, so the two screens agree.
 *
 * The pipeline records this on `sensor_info` itself (`primary` / `position`)
 * from the same sources. This module is the fallback for runs processed
 * before those fields existed: it re-derives them client-side rather than
 * leaving every older run blank until it is reprocessed.
 */
import { SolveData } from 'types/Run';
import { GroundSpeedSetup } from 'types/CalibrationParameters';
import {
  effectiveSetup,
  LegacySpeedFields,
  RunWheelRecordSource,
  SENSOR_FUNCTION,
  antDeviceNumber,
  runWheelSetup,
} from '../../CalibrationView/groundSpeedUtils';

export interface SensorRole {
  /** True/false once a record settles it; absent when nothing did. */
  primary?: boolean;
  /** SPEED_POSITION ordinal — speed and tyre-pressure sensors only. */
  position?: number;
}

export interface SensorRoleRun extends RunWheelRecordSource {
  solve?: SolveData | null;
}

export interface SensorRoleCalibration extends LegacySpeedFields {
  ground_speed_setup?: GroundSpeedSetup | null;
}

type RoleMap = Map<number, SensorRole>;

function entryFor(roles: RoleMap, deviceNumber: number): SensorRole {
  const existing = roles.get(deviceNumber);
  if (existing) return existing;
  const created: SensorRole = {};
  roles.set(deviceNumber, created);
  return created;
}

/**
 * Roles by ANT device number, from the run's own records with the
 * calibration filling positions the run never recorded (a legacy backfill
 * recovers ids and primary, never position).
 */
export function sensorRoles(
  run: SensorRoleRun | null | undefined,
  calibration?: SensorRoleCalibration | null,
): RoleMap {
  const roles: RoleMap = new Map();
  // Every wheel sensor this run knows about, from either record. The claim is
  // the wider net: a run with no `sensors_actual` has no synthesised setup at
  // all, and its claim is still the thing the cards are describing.
  const speedIds = new Set<number>();

  for (const claim of run?.sensors_intent?.sensors ?? []) {
    const deviceNumber = antDeviceNumber(claim.hash_id);
    const role = entryFor(roles, deviceNumber);
    role.primary = Boolean(claim.primary);
    if (claim.position) role.position = claim.position;
    if (claim.function === SENSOR_FUNCTION.SPEED) speedIds.add(deviceNumber);
  }

  const setup = runWheelSetup(run);
  const calSetup = calibration ? effectiveSetup(calibration.ground_speed_setup, calibration) : null;
  for (const sensor of setup?.sensors ?? []) speedIds.add(sensor.id_);

  // A legacy backfill recovers ids and primary but never wheel position, so
  // the calibration is where "front" and "rear" come from on older runs.
  for (const id of speedIds) {
    const role = entryFor(roles, id);
    if (role.position == null) {
      role.position = setup?.sensors?.find((s) => s.id_ === id)?.position
        || calSetup?.sensors?.find((s) => s.id_ === id)?.position
        || undefined;
    }
  }

  const wheels = [...speedIds];
  const setupPrimary = setup?.primary_id ?? null;
  if (setupPrimary != null && !wheels.some((id) => roles.get(id)?.primary)) {
    wheels.forEach((id) => { entryFor(roles, id).primary = id === setupPrimary; });
  }

  // What the solve actually read speed from outranks every claim.
  const solved = run?.solve?.selections?.speed?.ant_device_number;
  if (solved != null && speedIds.has(solved)) {
    wheels.forEach((id) => { entryFor(roles, id).primary = id === solved; });
  }

  return roles;
}

/**
 * The stored role for a card, falling back to the derived map. A stored
 * field wins outright — the pipeline saw the run being processed.
 */
export function roleFor(
  stored: { primary?: boolean | null; position?: number | null } | undefined,
  deviceNumber: number | null | undefined,
  derived: RoleMap,
): SensorRole {
  const fallback = deviceNumber != null ? derived.get(deviceNumber) : undefined;
  return {
    primary: stored?.primary ?? fallback?.primary,
    position: stored?.position ?? fallback?.position,
  };
}
